import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { BookVisitWidget } from "./BookVisitWidget";

import modules from "modules";
import WP from "WP";
import api from "api";
import { setCurrentView } from "store";
import { filterHotels, currentLanguage, scrollWindowTo } from "helpers";

import Container from "partials/Container";
import GridElement from "partials/GridElement";
import HotelCard from "partials/HotelCard/HotelCard";
import AccommodationAvailabilityChecker from "partials/AvailabilityChecker/AccommodationAvailabilityChecker";
import AdventureGliderList from "./AdventureGliderList";
import ErrorBoundary from "partials/ErrorBoundary";

import Media from "css/Media";

const AccommodationPage = (props) => {
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [adventures, setAdventures] = useState(null);
  const [initiateAdventureFiltering, setInitiateAdventureFiltering] = useState(0);

  const availabilityRef = useRef();
  const hotelsRef = useRef();
  const apartmentsRef = useRef();
  const adventuresRef = useRef();

  const { availability, page } = props;

  useEffect(() => {
    onHandlePagePrepare();
  }, []);

  useEffect(() => {
    onHandleFilterApartments();
  }, [filteredHotels]);

  useEffect(() => {
    getHotelsMap();
  }, [hotels]);

  //re render results when updating search
  useEffect(() => {
    getHotelsMap();
  }, [availability]);

  const onHandlePagePrepare = () => {
    Promise.all([fetchAdventuresData(), fetchAccommodationData(), fetchPageData()]);
  };

  const fetchAccommodationData = () => {
    return api.accommodationFetcher.getAll().then((response) => {
      setHotels(response.data);
    });
  };

  const fetchPageData = () => {
    return api.pageFetcher.getBySlug({ slug: props.match.path.replace("/en/", "") }).then((response) => {
      let page = response.data.shift();
      props.setCurrentView(page);
    });
  };

  const fetchAdventuresData = () => {
    return api.adventureFetcher.getAllPublic();
  };

  const setAdventureLength = (length) => {
    setAdventures(length);
  };

  const getHotelsMap = () => {
    if (!hotels.length) return;
    let filteredHotels;
    if (availability.searched) {
      filteredHotels = filterHotels(hotels, availability.hotels);
    } else {
      filteredHotels = hotels.map((hotel) => {
        return {
          hotel: hotel,
          visbookData: {},
        };
      });
    }
    setFilteredHotels(filteredHotels);
  };

  const onHandleFilterApartments = () => {
    let apartments = filteredHotels.filter(
      (item) =>
        item.hotel.acf.item_name === "Svalbard Hotell | Lodge" || item.hotel.acf.item_name === "Svalbard Hotel | Lodge"
    );
    setApartments(apartments);
  };

  const onHandleScrollToSection = (section) => {
    switch (section) {
      case "hotels":
        scrollWindowTo(hotelsRef.current, 500, "easeInOutQuad");
        break;
      case "leilighet":
        scrollWindowTo(apartmentsRef.current, 500, "easeInOutQuad");
        break;
      case "activities":
        scrollWindowTo(adventuresRef.current, 500, "easeInOutQuad");
        break;
    }
  };

  const filterAdventures = () => {
    setInitiateAdventureFiltering(initiateAdventureFiltering + 1);
  };

  if (!page && !hotels) return null;

  return (
    <Wrapper>
      <Container grid>
        <PageDescriptionGridElement colStart={{ default: 1, s: 5 }} colEnd={{ default: 8, s: 20 }} rowStart={1}>
          {!availability.searched ? (
            /* If the user has not searched yet */ currentLanguage() === "no" ? (
              <h1>
                Finn roen på et av våre anerkjente{" "}
                <span onClick={() => onHandleScrollToSection("hotels")} className="link">
                  hoteller
                </span>{" "}
                eller lei en komfortabel{" "}
                <span onClick={() => onHandleScrollToSection("leilighet")} className="link">
                  leilighet
                </span>{" "}
                i sentrum av Longyearbyen
              </h1>
            ) : (
              <h1>
                Here you will find tranquility at one of our renowned{" "}
                <span onClick={() => onHandleScrollToSection("hotels")} className="link">
                  hotels
                </span>{" "}
                or comfortable{" "}
                <span onClick={() => onHandleScrollToSection("leilighet")} className="link">
                  apartment
                </span>{" "}
                in the center of Longyearbyen.
              </h1>
            )
          ) : filteredHotels.length > 0 /* If the user has searched and gets results */ ? (
            <h1>
              <span onClick={() => onHandleScrollToSection("hotels")} className="link">
                {filteredHotels.length} {filteredHotels.length > 1 ? WP.trans.accommodations : WP.trans.accommodation}
              </span>
              {adventures > 0 && (
                <span>
                  {" " + WP.trans.and}
                  <span onClick={() => onHandleScrollToSection("activities")} className="link">
                    {" "}
                    {adventures} {adventures > 1 ? WP.trans.adventures : WP.trans.adventure}
                  </span>
                </span>
              )}
              <span> {WP.trans.isAvailable}</span>
            </h1>
          ) : (
            /* If the user has searched and gets no results */ <h1>{WP.trans.noMatches}</h1>
          )}
        </PageDescriptionGridElement>
      </Container>
      <AvailabilityWrapper ref={availabilityRef}>
        <AccommodationAvailabilityChecker filterHotels={filterHotels} filterAdventures={filterAdventures} />
      </AvailabilityWrapper>
      {filteredHotels.length > 0 && (
        <HotelWrapper ref={hotelsRef}>
          <Container grid>
            {filteredHotels
              .filter((item) => item.hotel.slug !== "svalbard-lodge")
              .map((item, idx) => {
                return (
                  <CardGridElement
                    colStart={{ default: 1, s: idx % 2 == 0 ? 3 : 13 }}
                    colEnd={{ default: 8, s: idx % 2 == 0 ? 12 : 22 }}
                    rowStart={{ default: idx + 1, s: 1 }}
                    key={idx}
                  >
                    <HotelCard
                      key={item.hotel.id}
                      hotel={item.hotel}
                      idx={idx}
                      visbookData={item.visbookData ? item.visbookData : null}
                    />
                  </CardGridElement>
                );
              })}
          </Container>
        </HotelWrapper>
      )}
      {apartments.length > 0 && (
        <ApartmentWrapper ref={apartmentsRef}>
          <Container grid>
            <GridElement colStart={{ default: 1, s: 3 }} colEnd={{ default: 8, s: 24 }} rowStart={1}>
              <StyledH2>{WP.trans.apartments}</StyledH2>
            </GridElement>
            {apartments.map((item, idx) => {
              return (
                <CardGridElement
                  colStart={{ default: 1, s: idx % 2 == 0 ? 3 : 13 }}
                  colEnd={{ default: 8, s: idx % 2 == 0 ? 12 : 22 }}
                  rowStart={{ default: idx + 2, s: 2 }}
                  key={idx}
                >
                  <HotelCard
                    key={item.hotel.id}
                    hotel={item.hotel}
                    idx={idx}
                    visbookData={item.visbookData ? item.visbookData : null}
                  />
                </CardGridElement>
              );
            })}
          </Container>
        </ApartmentWrapper>
      )}
      {availability.searched && (
        <div ref={adventuresRef}>
          <AdventureGliderList
            adventures={props.adventures}
            initiateAdventureFiltering={initiateAdventureFiltering}
            sendAdventureCount={setAdventureLength}
          />
        </div>
      )}
      {page && page.acf.modules && page.acf.modules.length ? (
        <Modules>
          <ErrorBoundary for="Accommodation Builder">
            {page &&
              page.acf.modules.map((module, i) => {
                return (
                  <div key={i} className={"module-" + module.acf_fc_layout}>
                    {React.createElement(modules[module.acf_fc_layout], {
                      data: module,
                    })}
                  </div>
                );
              })}
          </ErrorBoundary>
        </Modules>
      ) : null}
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  page: state.currentView,
  availability: state.availability,
  accommodation: state.accommodation.posts[WP.lang] ? state.accommodation.posts[WP.lang] : [],
  adventures: state.adventures.posts[WP.lang] ? state.adventures.posts[WP.lang] : [],
});

const actions = {
  setCurrentView,
};

export default connect(mapStateToProps, actions)(withRouter(AccommodationPage));

const Wrapper = styled.section`
  scroll-behavior: smooth;
  width: 100%;
  padding-top: ${(props) => props.theme.spacing.blockS};

  ${Media.greaterThan("s")`
        padding-top: 220px;
    `}
`;

const PageDescriptionGridElement = styled(GridElement)`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.elementS};

  ${Media.greaterThan("s")`
        margin-bottom: ${(props) => props.theme.spacing.elementXS};
    `}

  h1 {
    ${(props) => props.theme.fonts.h2};
  }

  span.link {
    color: ${(props) => props.theme.colors.button};
    transition: opacity 0.2s ease;
    text-decoration: underline;
    cursor: pointer;

    &:hover,
    &:focus,
    &:visited,
    &:active {
      opacity: 0.8s;
      text-decoration: none;
    }
  }
`;

const AvailabilityWrapper = styled.div`
  padding: ${(props) => props.theme.spacing.contentL} 0;
  width: 100%;
  background-color: ${(props) => props.theme.colors.negative};
`;

const HotelWrapper = styled.div`
  padding: ${(props) => props.theme.spacing.elementL} 0 ${(props) => props.theme.spacing.contentM};

  ${Media.greaterThan("s")`
        padding: ${(props) => props.theme.spacing.blockM} 0 0;
    `}
`;

const ApartmentWrapper = styled.div``;

const StyledH2 = styled.h2`
  padding-bottom: ${(props) => props.theme.spacing.elementXS};
  ${(props) => props.theme.fonts.h3}

  ${Media.lessThan("s")`
        ${(props) => props.theme.fonts.h1}
    `}
`;

const CardGridElement = styled(GridElement)`
  margin-bottom: ${(props) => props.theme.spacing.elementM};

  ${Media.greaterThan("s")`
        margin-bottom: ${(props) => props.theme.spacing.blockXL};
    `}
`;

const Modules = styled.section`
  padding-bottom: ${(props) => props.theme.spacing.blockXS};

  ${Media.greaterThan("s")`
        padding-bottom: ${(props) => props.theme.spacing.blockXL};
    `}
`;
